import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckIcon from '@mui/icons-material/Check'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import {
  Autocomplete,
  Button,
  Checkbox,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import { ReactNode, useEffect, useMemo, useState, cloneElement, Children } from 'react'
import { Control, FieldErrors } from 'react-hook-form'
import { FieldValidationRulesModal } from '../../../Models'
import { copyToClipboard } from '../../../Helpers/Util'
import LazyLoader from '../Loader/LazyLoader'
import ChipButton from '../ChipButton'
import useApiPaginatedOptions, { PaginatedFetchResult } from '../../../Hooks/useApiPaginatedOptions'
import useDebounce from '../../../Hooks/useDebounce'

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />
const checkedIcon = <CheckBoxIcon fontSize='small' />

interface MUIAutocompleteMultiSelectModal {
  usingFor?: string
  label: string
  LabelIcon?: ReactNode
  options: any[]
  labelKey: string
  valueKey?: string
  control?: Control
  name?: string
  disabled?: boolean
  error?: FieldErrors
  onInputChange?: any
  rules?: FieldValidationRulesModal
  value?: any
  width?: string
  limitTags?: number
  required?: boolean
  highlighSelectedField?: boolean
  id?: string
  disabledLabelsArr?: any
  disabledOptions?: any
  disabledOptionsByLable?: any
  sx?: any
  multiple?: boolean
  className?: string
  addOptionAll?: boolean
  testId?: string
  isBackButtonDisabled?: boolean
  statusKey?: string
  statusLabel?: string
  enableCopyPaste?: boolean
  /** Show a loading spinner while options are being fetched (eager/non-lazy path). */
  isOptionsLoading?: boolean
  /** Opt-in: drive options from a paginated API with lazy loading on scroll. */
  enableLazyLoad?: boolean
  fetchPage?: (
    page: number,
    pageSize: number,
    searchText: string,
    signal: AbortSignal,
  ) => Promise<PaginatedFetchResult<any>>
  pageSize?: number
  searchDebounceMs?: number
  scrollThresholdPx?: number
}

const MUIAutocompleteMultiSelect: React.FC<MUIAutocompleteMultiSelectModal> = ({
  usingFor = '',
  label,
  LabelIcon,
  options = [],
  labelKey,
  name = '',
  error,
  onInputChange,
  value,
  width = '100%',
  limitTags = 1,
  required = false,
  highlighSelectedField = true,
  id = 'checkboxes-tags-demo',
  disabledOptions = [],
  disabledOptionsByLable,
  sx = {},
  multiple = true,
  disabled = false,
  className = '',
  addOptionAll = false,
  testId = '',
  isBackButtonDisabled = false,
  valueKey,
  statusKey,
  statusLabel,
  enableCopyPaste = false,
  isOptionsLoading = false,
  enableLazyLoad = false,
  fetchPage,
  pageSize = 20,
  searchDebounceMs = 400,
  scrollThresholdPx = 60,
}: MUIAutocompleteMultiSelectModal) => {
  const [inputValue, setInputValue] = useState('')
  const [visibleCount, setVisibleCount] = useState(20)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [filteredOptions, setFilteredOptions] = useState<any[]>(options)
  const [copied, setCopied] = useState(false)
  const [open, setOpen] = useState(false)

  // ---- Lazy-load (API pagination) support ----
  const lazyKey = valueKey ?? 'key'
  const debouncedSearch = useDebounce(inputValue, searchDebounceMs)
  const noopFetch = useMemo<NonNullable<typeof fetchPage>>(
    () => async () => ({ data: [], total: 0 }),
    [],
  )
  const {
    options: lazyOptions,
    isLoading: lazyLoading,
    isFetchingMore: lazyFetchingMore,
    hasMore: lazyHasMore,
    error: lazyError,
    loadMore: lazyLoadMore,
    retry: lazyRetry,
  } = useApiPaginatedOptions<any>({
    fetchPage: fetchPage ?? noopFetch,
    getOptionId: (opt) => opt?.[lazyKey],
    pageSize,
    searchText: debouncedSearch,
    enabled: enableLazyLoad && open,
    logContext: `multiselect:${name}`,
  })

  const lazyMergedOptions = lazyOptions

  const handleLazyScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (
      scrollHeight - scrollTop - clientHeight <= scrollThresholdPx &&
      lazyHasMore &&
      !lazyFetchingMore &&
      !lazyLoading &&
      !lazyError
    ) {
      lazyLoadMore()
    }
  }

  useEffect(() => {
    if (options.length === 0) return
    if (inputValue.trim()) {
      setFilteredOptions(
        options.filter((opt: any) =>
          (opt[labelKey] ?? '').toLowerCase().includes(inputValue.toLowerCase()),
        ),
      )
    } else {
      setFilteredOptions(options)
    }
  }, [inputValue, options, labelKey])

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (
      scrollHeight - scrollTop - clientHeight < 50 &&
      !isLoadingMore &&
      visibleCount < filteredOptions.length
    ) {
      setIsLoadingMore(true)
      setTimeout(() => {
        setVisibleCount((prev) => Math.min(prev + 10, filteredOptions.length))
        setIsLoadingMore(false)
      }, 500)
    }
  }

  const visibleOptions = filteredOptions.slice(0, visibleCount)
  const visibleOptionsWithLoader = isLoadingMore
    ? [...visibleOptions, { isLoader: true }]
    : visibleOptions
  const handleSearchChange = (_event: any, value: string, reason: string) => {
    if (reason !== 'reset') {
      setInputValue(value)
    }
  }

  const handleOptionChange = (_event: any, newValue: any[], reason: any, details: any) => {
    if (details?.option?.[labelKey] === 'All') {
      if (value?.length && value?.length < options?.length) {
        const updatedOptions = options.filter((item: any) => item[labelKey] !== 'All')
        onInputChange(_event, updatedOptions)
        return
      }
      if (value?.length === 0) {
        const updatedOptions = options.filter((item: any) => item[labelKey] !== 'All')
        onInputChange(_event, updatedOptions)
      } else {
        onInputChange(_event, [])
      }
    } else {
      const updatedNewOptions = newValue.filter((item: any) => item[labelKey] !== 'All')
      onInputChange(_event, updatedNewOptions)
    }
  }

  const checkedAllOption = () =>
    Array.isArray(value) && value?.length > 0 && value?.length === options?.length

  const renderTags = (value: any[], getTagProps: any) => {
    return value.map((option, index) => {
      const tagProps = getTagProps({ index })
      return (
        <Chip
          {...tagProps}
          key={option.key || option._id}
          label={option[labelKey]}
          onDelete={tagProps.onDelete}
          disabled={option?.isDisabled ?? disabled}
          onClick={(event: any) => {
            event.stopPropagation()
            if (option.segmentUrl) {
              window.open(option.segmentUrl, '_blank')
            }
          }}
        />
      )
    })
  }

  const handleCopy = (e: any) => {
    e.preventDefault()
    e.stopPropagation()
    if (!Array.isArray(value) || value.length === 0) return
    const text = value.map((opt: any) => opt[labelKey] ?? '').join(', ')
    copyToClipboard(text, () => {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    })
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault()
    const text = event.clipboardData.getData('text')
    const tokens = text
      .split(',')
      .map((text) => text.trim())
      .filter(Boolean)
    const matched = options.filter((opt: any) =>
      tokens.some((token) => token.toLowerCase() === (opt[labelKey] ?? '').toLowerCase()),
    )
    if (matched.length === 0) return
    const existing = Array.isArray(value) ? value : []
    const merged = [
      ...existing,
      ...matched.filter(
        (matchedData: any) =>
          !existing.some(
            (existingData: any) => (existingData[labelKey] ?? '') === (matchedData[labelKey] ?? ''),
          ),
      ),
    ]
    onInputChange(event, merged)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const inputElement = event.target as HTMLInputElement
    const hasTypedText = inputElement?.value?.length > 0

    if (event.key === 'Backspace' && isBackButtonDisabled) {
      if (!hasTypedText && Array.isArray(value) && value.length > 0) {
        event.preventDefault()
        event.stopPropagation()
      }
    }
  }

  const handleOptions = () => {
    if (enableLazyLoad) {
      return lazyFetchingMore ? [...lazyMergedOptions, { isLoader: true }] : lazyMergedOptions
    }
    if (options.length === 0) return []
    const opts = addOptionAll
      ? [{ [labelKey]: 'All' }, ...visibleOptionsWithLoader]
      : visibleOptionsWithLoader

    return opts
  }

  const lazyNoOptionsText = (() => {
    if (lazyLoading) {
      return (
        <span data-testid='initial-loader'>
          <LazyLoader />
        </span>
      )
    }
    if (lazyError) {
      return (
        <span
          data-testid='error-state'
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
        >
          No options failed to load
          <Button
            size='small'
            variant='outlined'
            data-testid='retry-button'
            onMouseDown={(e) => e.preventDefault()}
            onClick={lazyRetry}
          >
            Retry
          </Button>
        </span>
      )
    }
    return <span data-testid='empty-state'>No options </span>
  })()

  return (
    <Autocomplete
      disabled={disabled}
      isOptionEqualToValue={(option, value) => {
        if (usingFor === 'metaAttributes') {
          return option.fieldName === value.fieldName
        }
        if (usingFor === 'attributePartnersName' || usingFor === 'assetTypeAllowedParents') {
          return option.key === value.key
        }
        if (usingFor === 'assetTypeTollgates') {
          return option.categoryName === value.categoryName
        }
        if (
          (labelKey &&
            option &&
            value &&
            option[labelKey] === value[labelKey] &&
            (valueKey ? option[valueKey] === value[valueKey] : true)) ||
          (!labelKey && option === value)
        ) {
          return true
        }
        return false
      }}
      {...(multiple
        ? {
            inputValue,
            onInputChange: handleSearchChange,
            onClose: () => {
              setOpen(false)
              setInputValue('')
            },
          }
        : {
            onInputChange: onInputChange,
          })}
      {...(enableLazyLoad
        ? {
            open,
            onOpen: () => setOpen(true),
            inputValue,
            onInputChange: handleSearchChange,
            filterOptions: (opts: any[]) => opts,
            loading: lazyLoading,
            loadingText: (
              <span style={{ display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={20} />
              </span>
            ),
          }
        : {})}
      multiple={multiple}
      id={id}
      options={handleOptions()}
      ListboxProps={{
        onScroll: enableLazyLoad ? handleLazyScroll : handleScroll,
        style: { maxHeight: 300, overflowY: 'auto' },
      }}
      disableCloseOnSelect={multiple}
      limitTags={limitTags}
      getLimitTagsText={(more: number) => `+${more} other${more > 1 ? 's' : ''}`}
      getOptionDisabled={(option: any) =>
        option.isLoader ||
        disabledOptions.includes(option?.[disabledOptionsByLable]) ||
        option?.isDisabled
      }
      data-testid={testId}
      getOptionLabel={(option: any) => (option.isLoader ? '' : (option?.[labelKey] ?? option))}
      value={value}
      onChange={addOptionAll ? handleOptionChange : onInputChange}
      renderOption={(props, option, { selected }) => {
        if (option.isLoader) {
          return (
            <li {...props} style={{ justifyContent: 'center', textAlign: 'center' }}>
              <LazyLoader />
            </li>
          )
        }
        if (addOptionAll && option?.[labelKey]?.toLowerCase() === 'all') {
          return (
            <li {...props}>
              <Checkbox checked={checkedAllOption()} />
              All
            </li>
          )
        }
        const statusValue = statusKey ? option?.[statusKey] : undefined
        const isInactive =
          typeof statusValue === 'string' && statusValue.toUpperCase() === 'INACTIVE'
        return (
          <li
            {...props}
            key={option.key || option._id}
            style={{
              ...(highlighSelectedField ? {} : { backgroundColor: selected ? 'transparent' : '' }),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center' }}>
              {multiple && (
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
              )}
              {option?.[labelKey] ?? option}
            </span>
            {statusValue !== undefined && (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  marginLeft: 8,
                  flexShrink: 0,
                }}
              >
                {statusLabel && (
                  <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>{statusLabel} •</span>
                )}
                <ChipButton
                  type={isInactive ? 'secondary' : 'success'}
                  label={isInactive ? 'Inactive' : 'Active'}
                  style={{ padding: '0px 8px' }}
                />
              </span>
            )}
          </li>
        )
      }}
      fullWidth
      sx={{ width: width, ...sx }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          data-testid='autocomplete-multi-select'
          error={!!error?.[name]}
          helperText={error?.[name]?.message as string}
          label={
            <div className='d-flex align-items-center'>
              {LabelIcon && <div className='d-flex align-items-center'>{LabelIcon}</div>}
              <div style={{ marginLeft: 5 }}>
                {' '}
                <span>
                  {label}
                  {required && <span style={{ color: '#D4351C' }}>*</span>}
                </span>
              </div>
            </div>
          }
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (() => {
              const copyButton =
                enableCopyPaste && multiple && Array.isArray(value) && value.length > 0 ? (
                  <Tooltip key='copy-btn' title={copied ? 'Copied!' : 'Copy selected'}>
                    <IconButton
                      size='small'
                      onClick={(e) => handleCopy(e)}
                      onMouseDown={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                      }}
                    >
                      {copied ? (
                        <CheckIcon fontSize='small' />
                      ) : (
                        <ContentCopyIcon fontSize='small' />
                      )}
                    </IconButton>
                  </Tooltip>
                ) : null
              return copyButton && params.InputProps.endAdornment
                ? cloneElement(
                    params.InputProps.endAdornment,
                    {},
                    ...Children.toArray(params.InputProps.endAdornment.props.children),
                    copyButton,
                  )
                : params.InputProps.endAdornment
            })(),
            onKeyDown: handleKeyDown,
            ...(enableCopyPaste && multiple ? { onPaste: handlePaste } : {}),
          }}
        />
      )}
      popupIcon={<KeyboardArrowDownIcon />}
      className={className}
      noOptionsText={
        enableLazyLoad ? (
          lazyNoOptionsText
        ) : isOptionsLoading ? (
          <span data-testid='options-loading' style={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={20} />
          </span>
        ) : (
          <span data-testid='no-options'>No options </span>
        )
      }
      renderTags={renderTags}
    />
  )
}

export default MUIAutocompleteMultiSelect
